import * as React from 'react';
import { CaretDown, CaretLeft, SidebarSimple } from '../../design-system/icons';
import { SimpleTooltip } from '../ui/tooltip';
import type { NavItem } from './AppSidebar';
import { useNavGroupCollapse } from './useSidebarCollapse';
import { NarrowOverlayBackdrop, useNarrowOverlayDismiss } from './narrowOverlay';

/**
 * Campaign-detail nav panel — the SECOND column in the Figma campaign-inner
 * layout. It sits to the right of the 64px main-nav rail (the collapsed
 * AppSidebar) and holds the per-campaign tabs.
 *
 * Figma ("App Admin" file, Outreach frames — supersedes the older node
 * 1177:33399 single-list layout this file originally shipped against):
 * "‹ Back to campaigns" header → collapsible "Outreach" and "Pipeline" group
 * accordions (icon + label header with a right-aligned caret, sub-items
 * indented beneath when expanded)
 * → divider → Campaign settings, with a collapse toggle pinned bottom-right.
 * Active item = stone-200 pill + black text; pending counts render as a
 * purple badge. Brand's flat, groupless item list (no `groupLabel`) is
 * unaffected — grouping only activates when `navBuilder` tags items.
 *
 * Collapse model mirrors AppSidebar: a fixed 40px icon zone + a label that clips
 * as the rail narrows (224px ↔ 64px), only the width animates.
 */
const EASE = 'ease-[cubic-bezier(0.32,0.72,0,1)]';
const RAIL_TRANSITION = `transition-[width] duration-[260ms] ${EASE} will-change-[width]`;
const ICON_ZONE = 'flex w-10 shrink-0 items-center justify-center'; // 40px → centres the glyph in the 64px collapsed rail (matches AppSidebar)
const BADGE_PURPLE = 'var(--brand-500)'; // #6C09F7 — Figma nav count badge
const GROUP_ITEM_INDENT = 'pl-4'; // Figma: group sub-items sit 16px right of the group header's icon

// Pinned to the footer group (below the divider), per Figma: Details + Settings
// form the bottom cluster (navBuilder marks Details with `dividerAbove`), with
// Shopify grouped alongside them.
const FOOTER_IDS = new Set(['details', 'settings', 'shopify']);

export interface CampaignNavSidebarProps {
  /** Campaign tab items (from buildNavItems). */
  items: NavItem[];
  /** `null` while the landing tab is undecided — no item is highlighted. */
  activeTab: string | null;
  activeSub?: string;
  onTabChange?: (id: string, sub?: string) => void;
  /** Back link → campaigns list. */
  onBack: () => void;
  backLabel?: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function CampaignNavSidebar({
  items,
  activeTab,
  activeSub,
  onTabChange,
  onBack,
  backLabel,
  collapsed = false,
  onToggleCollapse,
}: CampaignNavSidebarProps) {
  const labelFade = `transition-opacity duration-[260ms] ${EASE} ${collapsed ? 'opacity-0' : 'opacity-100'}`;
  const { isGroupCollapsed, toggleGroup } = useNavGroupCollapse();
  // Collapsed: clicking the empty rail (not a real control) expands it, so the
  // toggle button isn't the only way in. Buttons/links keep their behaviour.
  const handleRailExpand = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, [role="button"]')) return;
    onToggleCollapse?.();
  };
  const mainItems = items.filter((i) => !i.hide && !FOOTER_IDS.has(i.id));
  const footerItems = items.filter((i) => !i.hide && FOOTER_IDS.has(i.id));

  // Tag each main item with the accordion group it belongs to — everything
  // from a `groupLabel`-tagged item up to (not including) the next one. Group
  // membership also terminates at the next `dividerAbove` item, even when
  // that item defines its own new group: a divider marks a standalone band
  // (e.g. Reporting) that must never be swept into — or hidden by — the
  // group above it. Brand never sets `groupLabel` (see navBuilder's
  // `showOutreach`), so `group` stays undefined throughout and none of the
  // accordion logic below engages.
  const mainItemsWithGroup = mainItems.reduce<{
    list: { tab: NavItem; group: string | undefined }[];
    runningGroup: string | undefined;
  }>(
    (acc, tab) => {
      const group = tab.dividerAbove ? tab.groupLabel : (tab.groupLabel ?? acc.runningGroup);
      return { list: [...acc.list, { tab, group }], runningGroup: group };
    },
    { list: [], runningGroup: undefined },
  ).list;

  // A collapsed group whose own item is the active tab must still render —
  // never leave a user on a page whose nav entry is hidden. Only the
  // persisted per-group preference applies when the active item is elsewhere.
  const activeEntry = mainItemsWithGroup.find(
    ({ tab }) => activeTab === tab.id && (tab.sub === undefined || tab.sub === activeSub),
  );
  const activeGroup = activeEntry?.group;
  const keyForTab = (tab: NavItem) => (tab.sub ? `${tab.id}:${tab.sub}` : tab.id);

  // Tabs render as real anchors (href = the tab's `?tab` URL), so they get the
  // same browser affordances as the left-nav links: right-click "Open in new
  // tab", middle/modifier-click, and shareable URLs. A plain left-click stays a
  // client-side switch via onTabChange (which also resets sub-nav params);
  // modifier and middle clicks are left to the browser to open a new tab.
  const handleTabClick = (e: React.MouseEvent, id: string, sub?: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onTabChange?.(id, sub);
  };

  const renderItem = (tab: NavItem, indented = false) => {
    const isActive = activeTab === tab.id && (tab.sub === undefined || tab.sub === activeSub);
    const TabIcon = tab.icon;
    const className = [
      'relative flex h-10 w-full shrink-0 cursor-pointer items-center rounded-lg',
      // Expanded: clip the sliding label as the rail narrows. Collapsed: do NOT
      // clip, otherwise the floating count badge at the icon's top-right corner
      // (-top-0.5 + ring) gets cut off. The badge stays within the 64px rail, so
      // the aside's own overflow-hidden is the only bound and doesn't reach it.
      collapsed ? '' : 'overflow-hidden',
      indented && !collapsed ? GROUP_ITEM_INDENT : '',
      'border-none text-left text-body-md-regular font-medium no-underline transition-colors duration-150',
      isActive
        ? 'bg-stone-200 text-foreground'
        : 'text-muted-foreground hover:bg-stone-200/60 hover:text-foreground',
      tab.isPlaceholder ? 'pointer-events-none opacity-50' : '',
    ].join(' ');

    const node = (
      <a
        href={tab.sub ? `?tab=${tab.id}&sub=${tab.sub}` : `?tab=${tab.id}`}
        className={className}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={tab.isPlaceholder ? 'true' : undefined}
        title={tab.isPlaceholder ? `${tab.label} · ${tab.placeholderNote ?? 'Coming soon'}` : undefined}
        onClick={(e) => {
          if (tab.isPlaceholder) {
            e.preventDefault();
            return;
          }
          handleTabClick(e, tab.id, tab.sub);
        }}
        // Exempt from the public share view's click interception — this is
        // the CONTROL itself (not a container), so it's always allowed under
        // `isAllowedClick`'s rules. Its own `onClick` (via `onTabChange`)
        // decides what happens next: outside PublicCampaignShell this
        // attribute is inert (no interceptor exists to read it). See
        // campaign-detail/PublicCampaignShell.tsx.
        data-public-allowed=""
      >
        <span className={ICON_ZONE}>
          <TabIcon size={20} strokeWidth={1.6} />
        </span>
        <span className={`pointer-events-none whitespace-nowrap leading-none ${labelFade}`}>{tab.label}</span>
        {tab.count !== undefined && tab.count > 0 ? (
          <>
            <span
              className={`ml-auto mr-3 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-caption-medium font-semibold text-white ${labelFade}`}
              style={{ backgroundColor: BADGE_PURPLE }}
            >
              {tab.count}
            </span>
            {collapsed && (
              // Collapsed rail shows the actual count (capped 99+), mirroring
              // the chat-icon unread badge — a bare dot lost the number the
              // expanded rail shows. Floating over the icon's top-right corner.
              <span
                className="absolute -top-0.5 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-center text-[9px] font-black leading-none tabular-nums text-white ring-2 ring-background"
                style={{ backgroundColor: BADGE_PURPLE }}
                aria-label={`${tab.count} pending`}
              >
                {tab.count > 99 ? '99+' : tab.count}
              </span>
            )}
          </>
        ) : null}
      </a>
    );

    // Collapsed icons need a label tooltip (avoid mystery-meat nav).
    const element = collapsed ? (
      <SimpleTooltip key={tab.id} content={tab.label} side="right">
        {node}
      </SimpleTooltip>
    ) : (
      node
    );

    // A thin bar above the item, for a group split within mainItems (e.g.
    // Outreach above Pipeline/Reporting). Unlike the FOOTER_IDS split above,
    // this is opt-in per item via navBuilder's `dividerAbove` — mirrors
    // AppSidebar's handling of the same NavItem field, which this component
    // didn't previously honor (footer items only ever got their divider from
    // the FOOTER_IDS/footerItems split, never from this flag).
    if (tab.dividerAbove) {
      return (
        <React.Fragment key={tab.id}>
          <div className="my-1 h-px w-full shrink-0 bg-border" aria-hidden />
          {element}
        </React.Fragment>
      );
    }
    return element;
  };

  const { dismiss, toggleRef: expandToggleRef, trapRef } = useNarrowOverlayDismiss(!collapsed, onToggleCollapse);

  return (
    // Sticky column inside the shell content — sits below the 52px top bar and
    // fills the remaining viewport height while campaign content scrolls. The
    // wrapper reserves the in-flow width: below md an expanded nav keeps taking
    // only the 64px rail and overlays the content, so it can't force sideways scroll.
    <div
      className={[
        'sticky top-13 z-10 h-[calc(100vh-3.25rem)] w-[64px] shrink-0 self-start',
        RAIL_TRANSITION,
        collapsed ? '' : 'md:w-60',
      ].join(' ')}
    >
    {!collapsed && onToggleCollapse && <NarrowOverlayBackdrop onDismiss={dismiss} className="-z-10" />}
    <aside
      ref={trapRef}
      className={[
        'absolute inset-y-0 left-0 flex flex-col overflow-hidden',
        'border-r border-border bg-stone-50',
        RAIL_TRANSITION,
        collapsed ? 'w-[64px] cursor-pointer' : 'w-60 max-md:shadow-lg',
      ].join(' ')}
      aria-label="Campaign navigation"
      onClick={collapsed && onToggleCollapse ? handleRailExpand : undefined}
      // Public (no-login) share view: the shell intercepts every click outside
      // a `[data-public-allowed]` subtree and turns it into a login prompt.
      // Collapsing/expanding is pure layout, so the click-to-expand CONTAINER
      // is exempt while collapsed (that's when a bare rail click expands it).
      // See campaign-detail/PublicCampaignShell.tsx.
      {...(collapsed && onToggleCollapse ? { 'data-public-allowed': '' } : {})}
    >
      {/* Back to campaigns + collapse toggle on the right end — one row (when
          expanded). Collapsed shows just the back caret; the expand toggle
          moves to the bottom of the rail. */}
      <div className="flex h-14 shrink-0 items-center gap-1 overflow-hidden px-3">
        <button
          type="button"
          onClick={onBack}
          aria-label={`Back to ${backLabel ?? 'campaigns'}`}
          className={`flex h-10 min-w-0 flex-1 items-center overflow-hidden rounded-lg text-body-sm-medium text-muted-foreground transition-colors hover:text-foreground ${collapsed ? '' : 'gap-2'}`}
        >
          {/* Figma 1177:34655: the back caret sits ~12px from the left (no item
              inset), further left than the nav-item icons. Collapsed keeps the
              40px zone so the caret centres in the rail. */}
          <span className={collapsed ? ICON_ZONE : 'flex shrink-0 items-center pl-0.5'}>
            <CaretLeft size={18} />
          </span>
          <span className={`whitespace-nowrap ${labelFade}`}>Back</span>
        </button>
        {onToggleCollapse && !collapsed && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label="Collapse campaign nav"
            // Exempt from the public share view's click interception — pure
            // layout. See the aside's `data-public-allowed` comment above.
            data-public-allowed=""
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-stone-200 hover:text-foreground"
          >
            <SidebarSimple size={20} />
          </button>
        )}
      </div>
      <div className="mx-3 border-b border-border" />

      {/* Nav — main group, then Campaign settings IN-FLOW right after it (Figma:
          settings sits just below Live content with a divider, NOT pinned to the
          bottom). Items are top-aligned; the empty space falls below. */}
      <nav className="flex flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-3 py-5 scrollbar-none [&::-webkit-scrollbar]:hidden">
        {mainItemsWithGroup.map(({ tab, group }) => {
          const isGroupHeaderItem = Boolean(tab.groupLabel);
          // Persisted preference, overridden open whenever the active tab
          // lives in this group (see `activeGroup` above).
          const isActiveGroup = group !== undefined && group === activeGroup;
          const groupExpanded = group === undefined || isActiveGroup || !isGroupCollapsed(group);
          // Collapsed rail (64px, icon-only): there's no room for a
          // caret+label header, so it doesn't render (mirrors how `labelFade`
          // already hides text at this width) — and with no header to expand
          // it back, items must stay visible rather than get stuck hidden.
          const hideItem = !collapsed && group !== undefined && !groupExpanded;
          const GroupIcon = tab.groupIcon;
          return (
            <React.Fragment key={keyForTab(tab)}>
              {isGroupHeaderItem && !collapsed ? (
                <button
                  type="button"
                  onClick={() => {
                    // The active item's group always renders expanded (see
                    // `groupExpanded` above), so a header click here would be
                    // visibly a no-op — but without this guard it would still
                    // silently flip+persist the collapsed flag, making the
                    // group appear mysteriously collapsed after navigating
                    // away. Skip the write entirely while it's the active group.
                    if (isActiveGroup) return;
                    toggleGroup(tab.groupLabel as string);
                  }}
                  aria-expanded={groupExpanded}
                  // Exempt from the public share view's click interception —
                  // pure layout, same as the sidebar collapse toggles above.
                  // See `data-public-allowed`'s doc comment in
                  // campaign-detail/PublicCampaignShell.tsx.
                  data-public-allowed=""
                  className="flex h-10 w-full shrink-0 items-center rounded-lg text-left text-body-md-regular font-medium text-foreground transition-colors hover:bg-stone-200/60"
                >
                  <span className={ICON_ZONE}>{GroupIcon ? <GroupIcon size={20} strokeWidth={1.6} /> : null}</span>
                  <span className="whitespace-nowrap">{tab.groupLabel}</span>
                  <CaretDown
                    size={14}
                    className={`mr-3 ml-auto text-muted-foreground transition-transform duration-150 ${groupExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
              ) : null}
              {hideItem ? null : renderItem(tab, group !== undefined)}
            </React.Fragment>
          );
        })}
        {footerItems.length > 0 && (
          <>
            <div className="my-2 border-t border-border" />
            {footerItems.map((tab) => (
              <React.Fragment key={keyForTab(tab)}>{renderItem(tab)}</React.Fragment>
            ))}
          </>
        )}
      </nav>

      {/* Collapsed: expand toggle pinned to the bottom (clicking anywhere on the
          rail also expands it, but this is the explicit affordance). */}
      {onToggleCollapse && collapsed && (
        <div className="flex h-14 shrink-0 items-center justify-center px-3">
          <button
            type="button"
            ref={expandToggleRef}
            onClick={onToggleCollapse}
            aria-label="Expand campaign nav"
            data-public-allowed=""
            className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-stone-200 hover:text-foreground"
          >
            <SidebarSimple size={20} />
          </button>
        </div>
      )}
    </aside>
    </div>
  );
}
